'use client';

import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ShoppingCart, MessageSquare, Package, ChevronDown, Info } from 'lucide-react';
import { useQuotation } from '@/app/context/quotation-context';
import { useCart } from '@/app/context/cart-context';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';
import PriceDisplay from '@/components/ui/PriceDisplay';
import { getProductPackages, PackageWithImage, formatPackageText, getPackagePrice } from '@/lib/package-utils';
import { CartProduct } from '@/types/product';

// Simple utility function for class name conditionals
function cn(...inputs: (string | undefined)[]) {
  return twMerge(clsx(inputs));
}

// Define a string literal type for the mode prop
type ProductCardMode = 'product' | 'quotation' | 'store';

interface ProductCardProps {
  product: CartProduct;
  priority?: boolean;
  mode?: ProductCardMode;
  actions?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  isSelected?: boolean;
  onAddToQuotation?: () => void;
  onAddToCart?: (quantity: number) => void;
  className?: string;
  previewPackages?: PackageWithImage[];
  isPreview?: boolean;
  skipPackageFetch?: boolean; // Skip fetching packages if already provided
}

const ProductCard = memo(function ProductCard({
  product,
  priority = false,
  mode = 'product' as ProductCardMode,
  actions,
  onClick,
  href,
  isSelected = false,
  onAddToQuotation,
  onAddToCart,
  className,
  previewPackages,
  isPreview = false,
  skipPackageFetch = false
}: ProductCardProps) {
  const { addItem, isInQuotation } = useQuotation();
  const { addItem: addToCart, isInCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isMounted, setIsMounted] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [packages, setPackages] = useState<PackageWithImage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageWithImage | null>(null);
  const [showPackageSelect, setShowPackageSelect] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const packageSelectRef = useRef<HTMLDivElement>(null);
  
  
  // Only check quotation status if not in quotation mode
  const isInQuotationValue = mode !== 'quotation' ? isInQuotation(product.id, selectedPackage?.id) : false;

  // Determine if product is quotable and sellable
  const isQuotable = product.isQuotable !== false; // Default to true if undefined
  const isSellable = product.isSellable === true; // Default to false if undefined

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Determine if packages should be fetched based on product type and mode
  const shouldFetchPackages = () => {
    // For any sellable product with per_package pricing, fetch packages
    if (product.isSellable && product.pricingStrategy === 'per_package') {
      return true;
    }
    
    // For quotable products in appropriate modes
    if (product.isQuotable && (mode === 'product' || mode === 'quotation')) {
      return true;
    }
    
    return false;
  };

  // Use previewPackages if available, otherwise fetch packages if needed
  useEffect(() => {
    if (previewPackages) {
      // Use provided preview packages
      setPackages(previewPackages);
      if (previewPackages.length > 0 && !selectedPackage) {
        setSelectedPackage(previewPackages[0]);
      }
    } else if (product.id > 0 && shouldFetchPackages() && !skipPackageFetch) {
      fetchPackages();
    }
  }, [product.isQuotable, product.isSellable, product.pricingStrategy, product.id, mode, previewPackages, skipPackageFetch]);

  // Close package selector when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (packageSelectRef.current && !packageSelectRef.current.contains(event.target as Node)) {
        setShowPackageSelect(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchPackages = async () => {
    if (loadingPackages) return;
    
    setLoadingPackages(true);
    try {
      const productPackages = await getProductPackages(product.id);
      setPackages(productPackages);
      
      // Auto-select first package if available
      if (productPackages.length > 0) {
        console.log('[ProductCard] Auto-selecting first package for product', product.name, ':', productPackages[0]);
        setSelectedPackage(productPackages[0]);
      } else {
        console.log('[ProductCard] No packages found for product', product.name);
      }
    } catch (error) {
      console.error('Error fetching packages for product', product.id, ':', error);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleAddToQuotation = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const packageId = selectedPackage?.id;
    
    // For products with packages, require package selection
    if (packages.length > 0 && !packageId) {
      console.error('[ProductCard] ERROR: Product has packages but no package selected!');
      alert('Por favor selecciona un empaque antes de agregar a la cotización. Este producto tiene múltiples opciones de empaque.');
      return;
    }
    
    // Additional check - if this product should have packages but we have none loaded
    if (product.isQuotable && packages.length === 0 && !loadingPackages) {
      console.warn('[ProductCard] WARNING: Quotable product has no packages loaded. Adding without package data.');
    }
    
    const isCurrentlyInQuotation = isInQuotation(product.id, packageId);
    
    if (!isCurrentlyInQuotation) {
      console.log('[ProductCard] ===== ADDING TO QUOTATION =====');
      console.log('[ProductCard] Product:', product.name);
      console.log('[ProductCard] Quantity:', quantity);
      console.log('[ProductCard] SelectedPackage:', selectedPackage);
      console.log('[ProductCard] Packages available:', packages.length);
      
      setIsAdding(true);
      addItem(product, quantity, selectedPackage ?? undefined);
      
      // Reset animation state after a delay
      setTimeout(() => {
        setIsAdding(false);
      }, 1000);
    }
    
    // Call the custom handler if provided
    if (onAddToQuotation) {
      onAddToQuotation();
    }
  }, [addItem, isInQuotation, onAddToQuotation, product, quantity, selectedPackage, packages.length]);

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const packageId = selectedPackage?.id;
    
    // For products with packages, require package selection
    if (packages.length > 0 && !packageId) {
      alert('Por favor selecciona un empaque antes de agregar al carrito');
      return;
    }
    
    setIsAddingToCart(true);
    
    // Add the product to the cart with package data
    addToCart(product, quantity, selectedPackage ?? undefined);
    
    // Reset animation state after a delay
    setTimeout(() => {
      setIsAddingToCart(false);
    }, 1000);
    
    // Call the custom handler if provided
    if (onAddToCart) {
      onAddToCart(quantity);
    }
  }, [addToCart, onAddToCart, product, quantity, selectedPackage]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, value));
  };


  // Animation variants
  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: { scale: 1.02 }
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
    success: { scale: [1, 1.2, 1], backgroundColor: '#22c55e' }
  };

  const linkUrl = href || `/productos/${product.id}`;
  const requiresRenpre = product.name.includes('(*)');
  
  // Generate image URL from available data
  const generateImageUrl = () => {
    console.log('[ProductCard] Image selection for product', product.id, {
      isQuotable: product.isQuotable,
      hasPreviewPackages: !!previewPackages,
      loadingPackages,
      selectedPackage: selectedPackage?.packageType,
      hasSelectedPackageImage: !!selectedPackage?.imagePath,
      hasProductImage: !!(product.imageData && product.imageMimeType)
    });
    
    // Don't show any image until packages finish loading (prevent flickering)
    // Only apply this for quotable products that should have packages
    if (product.isQuotable && product.id > 0 && !previewPackages && loadingPackages) {
      console.log('[ProductCard] Waiting for packages to load, showing placeholder');
      return null;
    }
    
    // Priority 1: Use package image if a package is selected and has an image
    if (selectedPackage?.imagePath) {
      console.log('[ProductCard] Using selected package image:', selectedPackage.imagePath);
      return selectedPackage.imagePath;
    }
    
    // Priority 2: Use product image as fallback (main product image)
    if (product.imageData && product.imageMimeType) {
      console.log('[ProductCard] Using main product image as fallback');
      return `data:${product.imageMimeType};base64,${product.imageData}`;
    }
    
    // Priority 3: Fallback to old format if it exists
    if ('imageUrl' in product && typeof (product as any).imageUrl === 'string') {
      console.log('[ProductCard] Using legacy imageUrl');
      return (product as any).imageUrl;
    }
    
    // Priority 4: Default fallback image
    console.log('[ProductCard] Using default fallback image');
    return '/images/products/fallback_image.png';
  };
  
  const imageUrl = generateImageUrl();

  // For per_package products, don't show the card until packages are loaded
  // This prevents showing the main product image temporarily while packages load
  const isPerPackageProduct = product.pricingStrategy === 'per_package';
  const shouldWaitForPackages = isPerPackageProduct && product.id > 0 && !previewPackages && shouldFetchPackages();
  
  if (shouldWaitForPackages && loadingPackages) {
    // Return a loading skeleton that matches the card dimensions
    return (
      <div className={cn(
        "bg-white rounded-lg shadow-md flex flex-col transition-all duration-300 border border-gray-100 w-full overflow-hidden",
        className
      )}>
        {/* Loading skeleton */}
        <div className="relative w-full h-48 overflow-hidden bg-gray-100">
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-gray-300 rounded-lg"></div>
            </div>
          </div>
        </div>
        <div className="p-4 flex flex-col">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 rounded w-full"></div>
            <div className="h-3 bg-gray-300 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  // Unified card design for all modes (product, quotation, store)
  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      whileHover="whileHover"
      className={cn(
        "bg-white rounded-lg shadow-md flex flex-col transition-all duration-300 border border-gray-100 w-full",
        showPackageSelect ? "overflow-visible z-30" : "overflow-hidden",
        isSelected ? "ring-2 ring-indigo-500" : "hover:shadow-lg",
        className
      )}
      onClick={onClick}
    >
      {/* Product Image */}
      <div className="relative w-full h-48 overflow-hidden bg-gray-100">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={product.name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-contain hover:scale-105 transition-transform duration-300"
            quality={85}
            priority={priority}
          />
        ) : (
          // Loading placeholder - no image shown until packages load
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-gray-300 rounded-lg"></div>
            </div>
          </div>
        )}
        
        {/* Category badge */}
        {product.category && (
          <div className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-medium">
            {product.category}
          </div>
        )}
        
      </div>
      
      {/* Product Content */}
      <div className="p-3 flex flex-col flex-1">
        <Link href={linkUrl} className="hover:text-indigo-600 transition-colors" onClick={(e) => e.stopPropagation()}>
          <h3 className="font-medium text-base mb-1.5 text-gray-900 line-clamp-2">
            {product.name}
          </h3>
        </Link>

        {requiresRenpre && (
          <div
            className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
            title="Necesita inscripción en RENPRE (Registro Nacional de Precursores Químicos)"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Necesita inscripción en RENPRE</span>
          </div>
        )}

        {product.description && (
          <p className="text-gray-600 text-xs line-clamp-2 mb-2">
            {product.description}
          </p>
        )}

        {/* Bottom section - aligned to bottom of card */}
        <div className="mt-auto flex flex-col">
        {/* Package Selector - Dropdown approach */}
        {packages.length > 0 && (
          <div className="mb-2 relative" ref={packageSelectRef}>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Package className="w-3 h-3" />
              Empaque
            </label>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPackageSelect(!showPackageSelect);
              }}
              className="w-full p-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                {selectedPackage?.imagePath && (
                  <img 
                    src={selectedPackage.imagePath} 
                    alt={selectedPackage.packageType}
                    className="w-5 h-5 rounded object-cover border border-gray-200"
                  />
                )}
                <span className="text-sm">
                  {selectedPackage ? formatPackageText(selectedPackage) : 'Seleccionar empaque'}
                </span>
                {selectedPackage?.price_ars && (
                  <span className="text-xs text-green-600 font-medium ml-auto mr-2">
                    ${Number(selectedPackage.price_ars).toLocaleString('es-AR')} ARS
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                showPackageSelect ? 'rotate-180' : ''
              }`} />
            </button>
            
            {showPackageSelect && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {packages.map((pkg, index) => (
                  <button
                    key={pkg.id && pkg.id > 0 ? `pkg-${pkg.id}` : `preview-${pkg.packageType}-${pkg.weight}-${index}`}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedPackage(pkg);
                      setShowPackageSelect(false);
                    }}
                    className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-b-0 ${
                      selectedPackage?.id === pkg.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    {pkg.imagePath && (
                      <img 
                        src={pkg.imagePath} 
                        alt={pkg.packageType}
                        className="w-6 h-6 rounded object-cover border border-gray-200"
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">
                        {formatPackageText(pkg)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {pkg.weight && `Peso: ${pkg.weight}`}
                        {pkg.price_ars && (
                          <span className="ml-2 text-green-600 font-medium">
                            ${Number(pkg.price_ars).toLocaleString('es-AR')} ARS
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Legacy Physical Properties - show only if no packages */}
        {packages.length === 0 && (product.peso || product.estado) && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {product.peso && product.unidad && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                {Number(product.peso).toLocaleString()} {product.unidad}
              </span>
            )}
            {product.estado && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                {product.estado}
              </span>
            )}
          </div>
        )}
        
        
        {/* Action Buttons for main page view - disabled in preview mode */}
        {mode === 'product' && !isPreview && (
          <div className="pt-2 flex flex-col sm:flex-row gap-2">
            {/* Only show quotation button if product is not sellable or if it's specifically quotable */}
            {isQuotable && (!isSellable) && (
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                animate={isAdding ? "success" : "idle"}
                disabled={isInQuotationValue}
                onClick={handleAddToQuotation}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md shadow-sm",
                  isInQuotationValue
                    ? "bg-green-600 text-white hover:bg-green-500"
                    : "bg-indigo-600 text-white hover:bg-indigo-500"
                )}
                aria-label={isInQuotationValue ? "Producto agregado a cotización" : "Agregar producto a cotización"}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {isInQuotationValue ? "Cotizado" : "Cotizar"}
              </motion.button>
            )}
          </div>
        )}

        {/* Action Buttons for quotation mode - large prominent button */}
        {mode === 'quotation' && !isPreview && (
          <div className="pt-2">
            {isQuotable && (
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                animate={isAdding ? "success" : "idle"}
                disabled={isInQuotationValue}
                onClick={handleAddToQuotation}
                className={cn(
                  "w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md shadow-lg border-2 transition-all duration-300",
                  isInQuotationValue
                    ? "bg-green-600 text-white border-green-600 hover:bg-green-500 hover:border-green-500"
                    : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700 hover:shadow-xl transform hover:scale-105"
                )}
                aria-label={isInQuotationValue ? "Producto agregado a cotización" : "Agregar producto a cotización"}
              >
                <MessageSquare className="h-4 w-4" />
                <span className="font-bold">
                  {isInQuotationValue ? "✓ Agregado" : "Agregar a Cotización"}
                </span>
              </motion.button>
            )}
          </div>
        )}
        
        {/* Custom actions slot */}
        {actions && (
          <div className="pt-2">
            {actions}
          </div>
        )}
        
        {/* Price and Add to cart section for sellable products - disabled in preview mode */}
        {isSellable && (mode === 'store' || mode === 'product') && !isPreview && (
          <div className="pt-2">
            {/* Display price_ars (ARS) - from selected package or product */}
            {(() => {
              const showPrice = product.isSellable;
              const isPerPackage = product.pricingStrategy === 'per_package';
              const priceToShow = isPerPackage ? selectedPackage?.price_ars : product.price_ars;
              
              console.log('[ProductCard] Price Debug for product', product.id, {
                showPrice,
                isPerPackage,
                pricingStrategy: product.pricingStrategy,
                productPriceArs: product.price_ars,
                selectedPackage: selectedPackage ? {
                  id: selectedPackage.id,
                  packageType: selectedPackage.packageType,
                  price_ars: selectedPackage.price_ars
                } : null,
                priceToShow,
                packagesLength: packages.length
              });
              
              if (!showPrice) {
                console.log('[ProductCard] Not showing price - product not sellable');
                return null;
              }
              
              if (!priceToShow) {
                console.log('[ProductCard] Not showing price - no price to show');
                return (
                  <div className="mb-3">
                    <div className="text-xs text-gray-500">
                      {isPerPackage ? 'Configura precios de empaque' : 'Precio no configurado'}
                    </div>
                    <div className="h-4 mt-1"></div>
                  </div>
                );
              }
              
              return (
                <div className="mb-2">
                  <PriceDisplay
                    priceArs={Number(priceToShow)}
                    size="md"
                    className="font-bold text-indigo-700"
                  />
                  <div className="h-3 mt-0.5">
                    {isPerPackage && selectedPackage && (
                      <p className="text-xs text-gray-500">
                        Precio para {formatPackageText(selectedPackage)}
                      </p>
                    )}
                    {isPerPackage && !selectedPackage?.price_ars && (
                      <p className="text-xs text-yellow-600">
                        Selecciona un empaque para ver el precio
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
            
            {/* Quantity selector and add to cart button */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center">
                <label htmlFor={`quantity-${product.id}`} className="sr-only">
                  Cantidad
                </label>
                <input
                  type="number"
                  id={`quantity-${product.id}`}
                  className="border border-gray-300 rounded-md w-14 p-1 text-center text-sm"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                />
              </div>
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                animate={isAddingToCart ? "success" : "idle"}
                onClick={handleAddToCart}
                className="flex items-center px-2.5 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-500 transition-colors text-xs"
                aria-label="Reservar producto"
              >
                <ShoppingCart className="h-3.5 w-3.5 mr-1" />
                {isAddingToCart ? "Reservado" : "Reservar"}
              </motion.button>
            </div>
          </div>
        )}
        </div>{/* End of bottom section wrapper */}
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute inset-0 bg-indigo-500 opacity-10 pointer-events-none"></div>
      )}
    </motion.div>
  );
});

export default ProductCard; 